import React, { useContext, useState } from "react";
import { MessageSquare, X } from "lucide-react";
import { toast } from "../../utils/toast";

import AuthContext from "../../contexts/AuthCtx";
import { api } from "../../api/api";
import { captureEvent } from "../../analytics";

const CATEGORIES = [
  { id: "bug", label: "Bug", emoji: "🐛" },
  { id: "suggestion", label: "Suggestion", emoji: "💡" },
  { id: "improvement", label: "Amélioration", emoji: "✨" },
  { id: "other", label: "Autre", emoji: "💬" },
];

function FeedbackWidget() {
  const AuthCtx = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [category, setCategory] = useState("suggestion");
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Veuillez saisir un message.");
      return;
    }

    setIsSending(true);
    try {
      await api.post(
        "feedback",
        { category, message: message.trim() },
        {
          headers: { Authorization: `Bearer ${AuthCtx.userToken}` },
        }
      );
      captureEvent("feedback_submitted", { category, message: message.trim() });
      toast.success("Merci pour ton retour !");
      setMessage("");
      setIsOpen(false);
    } catch {
      toast.info(
        "Ton retour a été enregistré localement. L'envoi au serveur sera disponible prochainement."
      );
      setMessage("");
      setIsOpen(false);
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 z-50 flex items-center gap-2 rounded-lg bg-brandPurple px-3 py-2.5 font-josefin text-xs text-white shadow-lg transition hover:bg-primaryText sm:bottom-6 sm:right-6 sm:px-4 sm:py-3 sm:text-sm"
      >
        <MessageSquare size={18} />
        Aide-nous à améliorer
      </button>
    );
  }

  return (
    <div className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 left-4 z-50 w-auto rounded-2xl border border-greyLight/40 bg-white p-4 shadow-xl sm:bottom-6 sm:left-auto sm:w-[320px] sm:p-5">
      <div className="mb-4 flex items-start justify-between gap-2">
        <h3 className="font-josefin text-base font-semibold text-black">
          Aide-nous à améliorer ByeByeRegrets
        </h3>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="shrink-0 rounded p-1 text-secondaryText/50 hover:text-secondaryText"
          aria-label="Fermer"
        >
          <X size={16} />
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-3 flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => (
            <label
              key={cat.id}
              className={`flex cursor-pointer items-center gap-1 rounded-full border px-2.5 py-1 font-josefin text-xs transition ${
                category === cat.id
                  ? "border-brandPurple bg-brandPurple/10 text-brandPurple"
                  : "border-greyLight text-textMuted hover:border-brandPurple/40"
              }`}
            >
              <input
                type="radio"
                name="feedback-category"
                value={cat.id}
                checked={category === cat.id}
                onChange={() => setCategory(cat.id)}
                className="sr-only"
              />
              <span>{cat.emoji}</span>
              {cat.label}
            </label>
          ))}
        </div>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Décris ton retour…"
          rows={3}
          className="mb-3 w-full resize-none rounded-xl border border-greyLight/60 px-3 py-2 font-josefin text-sm text-secondaryText outline-none focus:border-brandPurple"
        />

        <button
          type="submit"
          disabled={isSending}
          className="ml-auto block rounded-lg bg-sageDark px-5 py-2 font-josefin text-sm text-white transition hover:bg-sage disabled:opacity-50"
        >
          {isSending ? "Envoi…" : "Envoyer"}
        </button>
      </form>
    </div>
  );
}

export default FeedbackWidget;
